import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { Water } from "three/addons/objects/Water.js";
import { Sky } from "three/addons/objects/Sky.js";

const container = document.getElementById("sll-ocean-background");

if (!container) {
  throw new Error("Missing #sll-ocean-background container.");
}

/* Mode: 'full' = ocean+boat+crosshair, 'crosshair' = crosshair only (transparent) */
const crosshairOnly = window.SLL_BKG_MODE === 'crosshair';

let camera;
let scene;
let renderer;
let water;
let renderTarget;
let boat;
let overlayScene;
let overlayCamera;
let overlayGroup;
let overlaySquare;
let overlayLineTop;
let overlayLineBottom;
let overlayLineLeft;
let overlayLineRight;
let overlaySquareMaterial;
let overlayLineMaterial;
let overlayLongitudeLabel;
let overlayLongitudeTexture;
let overlayLongitudeCanvas;
let overlayLongitudeContext;
let overlayLatitudeLabel;
let overlayLatitudeTexture;
let overlayLatitudeCanvas;
let overlayLatitudeContext;
let isMobile;
let lockedHeight;

const clock = new THREE.Clock();
const sun = new THREE.Vector3();
const boatModelUrl = window.SLL_BOAT_MODEL_URL || null;
const baseCameraPosition = new THREE.Vector3(-62, 12, -56);
const baseCameraTarget = new THREE.Vector3(0, 9, 0);
const boatCenter = new THREE.Vector3(0, -3.5, 0);
const boatRedColor = new THREE.Color(0xff1a1a);
const introCamera = {
  active: true,
  elapsed: 0,
  duration: 2.5,
  startPosition: new THREE.Vector3(-120, 520, 760),
  endPosition: baseCameraPosition.clone(),
  startTarget: new THREE.Vector3(0, 4, 0),
  endTarget: baseCameraTarget.clone(),
  startFov: 58,
  endFov: 85,
};
const introScrollBlend = {
  active: false,
  progress: 0,
  duration: 1.4,
};
const scrollTravel = {
  camera: new THREE.Vector3(-160, 0, -160),
  target: new THREE.Vector3(-160, 0, -160),
};
const scrollState = {
  progress: 0,
  smoothProgress: 0,
};
const pointerState = {
  targetX: window.innerWidth * 0.5,
  targetY: window.innerHeight * 0.5,
  currentX: window.innerWidth * 0.5,
  currentY: window.innerHeight * 0.5,
  isPointerHover: false,
};
const parameters = {
  elevation: -18,
  azimuth: 160,
};

init();
animate();

function init() {
  isMobile = window.matchMedia('(max-width: 749px)').matches;

  /* Crosshair-only mode: lightweight transparent canvas, overlay only */
  if (crosshairOnly) {
    if (isMobile) return; /* No crosshair on mobile */
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.autoClear = false;
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);
    container.style.pointerEvents = 'none';
    container.style.background = 'transparent';

    createOverlayScene();
    setupPointerTracking();

    window.addEventListener("resize", () => {
      renderer.setSize(container.clientWidth, container.clientHeight);
      overlayCamera.left = -container.clientWidth * 0.5;
      overlayCamera.right = container.clientWidth * 0.5;
      overlayCamera.top = container.clientHeight * 0.5;
      overlayCamera.bottom = -container.clientHeight * 0.5;
      overlayCamera.updateProjectionMatrix();
    }, { passive: true });
    return;
  }

  /* Full mode: ocean + boat + crosshair */
  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.5 : 2));

  /* Lock height on mobile to prevent Chrome toolbar collapse from resizing */
  if (isMobile) {
    lockedHeight = window.innerHeight;
  }

  const initH = isMobile ? lockedHeight : container.clientHeight;
  renderer.setSize(container.clientWidth, initH);

  if (isMobile) {
    /* Lock canvas to exact pixel size — container background (#010104) fills any gap */
    renderer.domElement.style.width = container.clientWidth + 'px';
    renderer.domElement.style.height = lockedHeight + 'px';
  }
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.18;
  renderer.autoClear = false;
  container.appendChild(renderer.domElement);

  scene = new THREE.Scene();
  scene.background = new THREE.Color(0x010104);

  camera = new THREE.PerspectiveCamera(
    isMobile ? introCamera.endFov : introCamera.startFov,
    container.clientWidth / container.clientHeight,
    1,
    20000
  );
  camera.position.copy(introCamera.startPosition);
  camera.lookAt(introCamera.startTarget);

  const waterGeometry = new THREE.PlaneGeometry(10000, 10000);

  const waterTexSize = isMobile ? 256 : 512;

  water = new Water(waterGeometry, {
    textureWidth: waterTexSize,
    textureHeight: waterTexSize,
    waterNormals: new THREE.TextureLoader().load(
      "https://threejs.org/examples/textures/waternormals.jpg",
      (texture) => {
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
      }
    ),
    sunDirection: new THREE.Vector3(),
    sunColor: 0xff2020,
    waterColor: 0x010104,
    distortionScale: 3.2,
    fog: false,
  });
  water.rotation.x = -Math.PI / 2;
  scene.add(water);

  const sky = new Sky();
  sky.scale.setScalar(10000);
  scene.add(sky);

  const skyUniforms = sky.material.uniforms;
  skyUniforms.turbidity.value = 2.2;
  skyUniforms.rayleigh.value = 0.04;
  skyUniforms.mieCoefficient.value = 0.04;
  skyUniforms.mieDirectionalG.value = 0.97;

  const pmremGenerator = new THREE.PMREMGenerator(renderer);
  const sceneEnv = new THREE.Scene();

  updateSun(sky, pmremGenerator, sceneEnv);
  if (!isMobile) {
    createOverlayScene();
    setupPointerTracking();
  }

  if (boatModelUrl) {
    createBoat();
    loadBoatModel();
  }

  window.addEventListener("resize", onWindowResize, { passive: true });
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();
}

function createOverlayScene() {
  overlayScene = new THREE.Scene();
  overlayCamera = new THREE.OrthographicCamera(
    -container.clientWidth * 0.5,
    container.clientWidth * 0.5,
    container.clientHeight * 0.5,
    -container.clientHeight * 0.5,
    -10,
    10
  );
  overlayGroup = new THREE.Group();
  overlayScene.add(overlayGroup);

  overlaySquareMaterial = new THREE.LineBasicMaterial({
    color: 0xb4b4b4,
    transparent: true,
    opacity: 0.72,
    toneMapped: false,
  });
  overlayLineMaterial = new THREE.LineBasicMaterial({
    color: 0xb4b4b4,
    transparent: true,
    opacity: 0.52,
    toneMapped: false,
  });

  overlaySquare = new THREE.LineLoop(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-12, -12, 0),
      new THREE.Vector3(12, -12, 0),
      new THREE.Vector3(12, 12, 0),
      new THREE.Vector3(-12, 12, 0),
    ]),
    overlaySquareMaterial
  );

  overlayLineTop = new THREE.Line(new THREE.BufferGeometry(), overlayLineMaterial);
  overlayLineBottom = new THREE.Line(new THREE.BufferGeometry(), overlayLineMaterial);
  overlayLineLeft = new THREE.Line(new THREE.BufferGeometry(), overlayLineMaterial);
  overlayLineRight = new THREE.Line(new THREE.BufferGeometry(), overlayLineMaterial);

  overlayLongitudeCanvas = document.createElement("canvas");
  overlayLongitudeCanvas.width = 512;
  overlayLongitudeCanvas.height = 64;
  overlayLongitudeContext = overlayLongitudeCanvas.getContext("2d");
  overlayLongitudeTexture = new THREE.CanvasTexture(overlayLongitudeCanvas);
  overlayLongitudeTexture.colorSpace = THREE.SRGBColorSpace;

  overlayLongitudeLabel = new THREE.Mesh(
    new THREE.PlaneGeometry(220, 20),
    new THREE.MeshBasicMaterial({
      map: overlayLongitudeTexture,
      transparent: true,
      opacity: 0.5,
      toneMapped: false,
    })
  );
  overlayLongitudeLabel.rotation.z = -Math.PI * 0.5;

  overlayLatitudeCanvas = document.createElement("canvas");
  overlayLatitudeCanvas.width = 512;
  overlayLatitudeCanvas.height = 64;
  overlayLatitudeContext = overlayLatitudeCanvas.getContext("2d");
  overlayLatitudeTexture = new THREE.CanvasTexture(overlayLatitudeCanvas);
  overlayLatitudeTexture.colorSpace = THREE.SRGBColorSpace;

  overlayLatitudeLabel = new THREE.Mesh(
    new THREE.PlaneGeometry(220, 20),
    new THREE.MeshBasicMaterial({
      map: overlayLatitudeTexture,
      transparent: true,
      opacity: 0.5,
      toneMapped: false,
    })
  );

  overlayGroup.add(
    overlaySquare,
    overlayLineTop,
    overlayLineBottom,
    overlayLineLeft,
    overlayLineRight,
    overlayLongitudeLabel,
    overlayLatitudeLabel
  );
}

function setupPointerTracking() {
  window.addEventListener(
    "mousemove",
    (event) => {
      updatePointerTarget(event.clientX, event.clientY);
    },
    { passive: true }
  );

  window.addEventListener(
    "touchmove",
    (event) => {
      if (event.touches.length === 0) {
        return;
      }
      updatePointerTarget(event.touches[0].clientX, event.touches[0].clientY);
    },
    { passive: true }
  );
}

function updatePointerTarget(clientX, clientY) {
  pointerState.targetX = clientX;
  pointerState.targetY = clientY;

  const hovered = document.elementFromPoint(clientX, clientY);
  if (!hovered) {
    pointerState.isPointerHover = false;
    return;
  }

  const style = window.getComputedStyle(hovered);
  pointerState.isPointerHover = style.cursor === "pointer";
}

function updateCursorOverlay(deltaSeconds) {
  const blend = 1 - Math.exp(-deltaSeconds * 12);
  pointerState.currentX += (pointerState.targetX - pointerState.currentX) * blend;
  pointerState.currentY += (pointerState.targetY - pointerState.currentY) * blend;

  const rect = container.getBoundingClientRect();
  const x = THREE.MathUtils.clamp(pointerState.currentX - rect.left, 0, rect.width);
  const y = THREE.MathUtils.clamp(pointerState.currentY - rect.top, 0, rect.height);
  const half = 12;
  const ox = x - rect.width * 0.5;
  const oy = rect.height * 0.5 - y;
  const activeColor = pointerState.isPointerHover ? 0xff1a1a : 0xb4b4b4;

  overlaySquareMaterial.color.setHex(activeColor);
  overlayLineMaterial.color.setHex(activeColor);
  overlayLineMaterial.opacity = 0.5;

  overlayGroup.position.set(ox, oy, 0);

  overlayLineTop.geometry.setFromPoints([
    new THREE.Vector3(0, half, 0),
    new THREE.Vector3(0, rect.height * 0.5 - oy, 0),
  ]);
  overlayLineBottom.geometry.setFromPoints([
    new THREE.Vector3(0, -half, 0),
    new THREE.Vector3(0, -rect.height * 0.5 - oy, 0),
  ]);
  overlayLineLeft.geometry.setFromPoints([
    new THREE.Vector3(-half, 0, 0),
    new THREE.Vector3(-(x), 0, 0),
  ]);
  overlayLineRight.geometry.setFromPoints([
    new THREE.Vector3(half, 0, 0),
    new THREE.Vector3(rect.width - x, 0, 0),
  ]);

  updateOverlayLongitudeLabel(x, rect, oy, activeColor);
  updateOverlayLatitudeLabel(y, rect, ox, activeColor);
}

function updateOverlayLongitudeLabel(x, rect, oy, activeColor) {
  const normalizedX = rect.width === 0 ? 0.5 : x / rect.width;
  const centeredX = normalizedX - 0.5;
  const eastValue = centeredX * 120;
  const label = formatCoordinateDms(eastValue, "E", "W");
  const colorHex = `#${activeColor.toString(16).padStart(6, "0")}`;
  const sideOffset = normalizedX > 0.5 ? -11 : 11;

  overlayLongitudeContext.clearRect(
    0,
    0,
    overlayLongitudeCanvas.width,
    overlayLongitudeCanvas.height
  );
  overlayLongitudeContext.fillStyle = colorHex;
  overlayLongitudeContext.font = '700 30px "Helvetica Neue", Arial, sans-serif';
  overlayLongitudeContext.textAlign = "center";
  overlayLongitudeContext.textBaseline = "middle";
  overlayLongitudeContext.fillText(
    label,
    overlayLongitudeCanvas.width * 0.5,
    overlayLongitudeCanvas.height * 0.5
  );
  overlayLongitudeTexture.needsUpdate = true;

  overlayLongitudeLabel.position.set(
    sideOffset,
    rect.height * 0.5 - oy - 61,
    0
  );
}

function updateOverlayLatitudeLabel(y, rect, ox, activeColor) {
  const normalizedY = rect.height === 0 ? 0.5 : y / rect.height;
  const centeredY = 0.5 - normalizedY;
  const northValue = centeredY * 80;
  const label = formatCoordinateDms(northValue, "N", "S");
  const colorHex = `#${activeColor.toString(16).padStart(6, "0")}`;
  const verticalOffset = normalizedY > 0.5 ? 11 : -11;

  overlayLatitudeContext.clearRect(
    0,
    0,
    overlayLatitudeCanvas.width,
    overlayLatitudeCanvas.height
  );
  overlayLatitudeContext.fillStyle = colorHex;
  overlayLatitudeContext.font = '700 30px "Helvetica Neue", Arial, sans-serif';
  overlayLatitudeContext.textAlign = "left";
  overlayLatitudeContext.textBaseline = "middle";
  overlayLatitudeContext.fillText(
    label,
    0,
    overlayLatitudeCanvas.height * 0.5
  );
  overlayLatitudeTexture.needsUpdate = true;

  overlayLatitudeLabel.position.set(
    -rect.width * 0.5 - ox + 123,
    verticalOffset,
    0
  );
}

function formatCoordinateDms(decimalValue, positiveHemisphere, negativeHemisphere) {
  const hemisphere = decimalValue >= 0 ? positiveHemisphere : negativeHemisphere;
  const absoluteValue = Math.abs(decimalValue);
  const degrees = Math.floor(absoluteValue);
  const minutesFloat = (absoluteValue - degrees) * 60;
  const minutes = Math.floor(minutesFloat);
  let seconds = Math.round((minutesFloat - minutes) * 60);
  let normalizedMinutes = minutes;
  let normalizedDegrees = degrees;

  if (seconds === 60) {
    seconds = 0;
    normalizedMinutes += 1;
  }

  if (normalizedMinutes === 60) {
    normalizedMinutes = 0;
    normalizedDegrees += 1;
  }

  return `${normalizedDegrees}° ${String(normalizedMinutes).padStart(2, "0")}′ ${String(
    seconds
  ).padStart(2, "0")}″ ${hemisphere}`;
}

function createBoat() {
  boat = new THREE.Group();
  boat.rotation.y = -0.16 + Math.PI;
  scene.add(boat);
}

// UV-mapped logo configs (per-mesh-name).
const LOGO_CONFIG = {
  Object048: {
    svgUrl: window.SLL_LOGO_SOLALUNE_URL || "./logo-solalune.svg",
    scale: 0.5 * 1.15 * 1.10 * 1.10,
    offsetY: 1,
    offsetX: 0,
    bgColor: "#ff1a1a",
    flipU: true,
    flipV: false,
    mirrorBackFace: true,
  },
};

// Plane decals: independent quads parented to boat, displaying a logo on the surface.
const DECAL_CONFIG = [
  {
    target: "Object025",
    svgUrl: window.SLL_LOGO_SLL_URL || "./logo-sll.svg",
    size: 2.8,
    offsetX: 15.6,
    offsetY: 1.8,
    offsetZ: 8,
    rotationX: -83,
    rotationY: 143,
    rotationZ: 154,
  },
  {
    target: "Object025",
    svgUrl: window.SLL_LOGO_SLL_URL || "./logo-sll.svg",
    size: 2.8,
    offsetX: 15.6,
    offsetY: 1.8,
    offsetZ: -8,
    rotationX: 83,
    rotationY: -165,
    rotationZ: 21,
  },
  {
    target: "Object025",
    svgUrl: window.SLL_LOGO_N11_URL || "./logo-n11.svg",
    size: 3.9,
    offsetX: -14.3,
    offsetY: 1.4,
    offsetZ: 9.9,
    rotationX: 75,
    rotationY: 23,
    rotationZ: -18,
  },
  {
    target: "Object025",
    svgUrl: window.SLL_LOGO_N11_URL || "./logo-n11.svg",
    size: 3.9,
    offsetX: -14.3,
    offsetY: 1.4,
    offsetZ: -9.9,
    rotationX: -77,
    rotationY: -23,
    rotationZ: -150,
  },
];

function applyLogoToMesh(child, opts) {
  const geo = child.geometry;
  geo.computeBoundingBox();
  const pos = geo.attributes.position;

  // Build local frame on the actual surface plane (using avg vertex normal)
  if (!geo.attributes.normal) geo.computeVertexNormals();
  const nrm = geo.attributes.normal;
  const avgN = new THREE.Vector3();
  for (let i = 0; i < nrm.count; i++) {
    avgN.x += nrm.getX(i); avgN.y += nrm.getY(i); avgN.z += nrm.getZ(i);
  }
  avgN.normalize();
  const worldUp = Math.abs(avgN.y) < 0.9
    ? new THREE.Vector3(0, 1, 0)
    : new THREE.Vector3(1, 0, 0);
  const tangent = new THREE.Vector3().crossVectors(worldUp, avgN).normalize();
  const bitangent = new THREE.Vector3().crossVectors(avgN, tangent).normalize();

  const projU = new Float32Array(pos.count);
  const projV = new Float32Array(pos.count);
  let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
  const tmp = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    tmp.set(pos.getX(i), pos.getY(i), pos.getZ(i));
    const u = tmp.dot(tangent);
    const v = tmp.dot(bitangent);
    projU[i] = u; projV[i] = v;
    if (u < minU) minU = u; if (u > maxU) maxU = u;
    if (v < minV) minV = v; if (v > maxV) maxV = v;
  }
  const spanU = maxU - minU || 1;
  const spanV = maxV - minV || 1;
  const surfaceTall = spanV > spanU;
  const longSpan = Math.max(spanU, spanV);
  const shortSpan = Math.min(spanU, spanV);

  // Canvas: TRANSPARENT bg; bgColor is a uniform managed by THREE.Color so it
  // matches MeshBasicMaterial(color) exactly.
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = Math.round(512 * longSpan / shortSpan);
  const ctx = canvas.getContext("2d");
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const bgColorUniform = new THREE.Color(opts.bgColor);

  let logoImg = null;

  function redraw() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (logoImg) {
      const svgRatio = logoImg.naturalWidth / logoImg.naturalHeight;
      const drawW = canvas.width * opts.scale;
      const drawH = drawW / svgRatio;
      const cx = canvas.width / 2 + drawW * (opts.offsetX || 0);
      const cy = canvas.height / 2 + drawH * (opts.offsetY || 0);
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(((opts.rotation || 0) * Math.PI) / 180);
      ctx.drawImage(logoImg, -drawW / 2, -drawH / 2, drawW, drawH);
      ctx.restore();
    }
    bgColorUniform.set(opts.bgColor);
    texture.needsUpdate = true;
  }

  fetch(opts.svgUrl)
    .then((r) => r.text())
    .then((svgText) => {
      const blob = new Blob([svgText], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      logoImg = new Image();
      logoImg.onload = () => {
        URL.revokeObjectURL(url);
        redraw();
      };
      logoImg.src = url;
    })
    .catch((err) => console.error(`[logo] fetch ${opts.svgUrl} failed`, err));

  const uvArr = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    let nu = (projU[i] - minU) / spanU;
    let nv = (projV[i] - minV) / spanV;
    if (opts.flipU) nu = 1 - nu;
    if (opts.flipV) nv = 1 - nv;
    if (surfaceTall) {
      uvArr[i * 2]     = nu;
      uvArr[i * 2 + 1] = nv;
    } else {
      uvArr[i * 2]     = nv;
      uvArr[i * 2 + 1] = nu;
    }
  }
  geo.setAttribute("uv", new THREE.BufferAttribute(uvArr, 2));

  child.material = new THREE.ShaderMaterial({
    side: THREE.DoubleSide,
    uniforms: {
      map: { value: texture },
      bgColor: { value: bgColorUniform },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D map;
      uniform vec3 bgColor;
      varying vec2 vUv;
      vec3 linearToSRGB(vec3 c) {
        bvec3 hi = greaterThan(c, vec3(0.0031308));
        vec3 lo = c * 12.92;
        vec3 high = 1.055 * pow(c, vec3(1.0/2.4)) - 0.055;
        return mix(lo, high, vec3(hi));
      }
      void main() {
        vec2 uv = vUv;
        ${opts.mirrorBackFace ? "if (!gl_FrontFacing) uv.x = 1.0 - uv.x;" : ""}
        vec4 logo = texture2D(map, uv);
        vec3 color = mix(bgColor, logo.rgb, logo.a);
        // ShaderMaterial doesn't auto-apply the renderer's output color space;
        // do it manually so reds match MeshBasicMaterial.
        color = linearToSRGB(color);
        gl_FragColor = vec4(color, 1.0);
      }
    `,
  });
}

function addLogoPlane(targetMesh, opts) {
  const geo = targetMesh.geometry;
  geo.computeBoundingBox();
  if (!geo.attributes.normal) geo.computeVertexNormals();

  targetMesh.updateMatrixWorld(true);
  boat.updateMatrixWorld(true);

  const center = new THREE.Vector3();
  geo.boundingBox.getCenter(center);
  targetMesh.localToWorld(center);
  boat.worldToLocal(center);

  const nrm = geo.attributes.normal;
  const avgN = new THREE.Vector3();
  for (let i = 0; i < nrm.count; i++) {
    avgN.x += nrm.getX(i); avgN.y += nrm.getY(i); avgN.z += nrm.getZ(i);
  }
  avgN.normalize();
  const meshWorldQuat = new THREE.Quaternion();
  targetMesh.getWorldQuaternion(meshWorldQuat);
  avgN.applyQuaternion(meshWorldQuat);
  const boatWorldQuat = new THREE.Quaternion();
  boat.getWorldQuaternion(boatWorldQuat);
  avgN.applyQuaternion(boatWorldQuat.invert());
  avgN.normalize();

  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");
  let texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;

  let logoAspect = 1;
  function parseSvgAspect(svgText) {
    const root = new DOMParser().parseFromString(svgText, "image/svg+xml").documentElement;
    const viewBox = root.getAttribute("viewBox");
    if (viewBox) {
      const [, , w, h] = viewBox.split(/[\s,]+/).map(Number);
      if (w > 0 && h > 0) return w / h;
    }
    const w = parseFloat(root.getAttribute("width"));
    const h = parseFloat(root.getAttribute("height"));
    if (w > 0 && h > 0) return w / h;
    return 1;
  }

  fetch(opts.svgUrl)
    .then((r) => r.text())
    .then((svgText) => {
      logoAspect = parseSvgAspect(svgText);
      const blob = new Blob([svgText], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        canvas.width = 512;
        canvas.height = Math.round(512 / logoAspect);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        // Recreate texture so GPU picks up the new canvas dimensions
        texture.dispose();
        texture = new THREE.CanvasTexture(canvas);
        texture.colorSpace = THREE.SRGBColorSpace;
        plane.material.map = texture;
        plane.material.needsUpdate = true;
        applyTransform();
      };
      img.src = url;
    })
    .catch((err) => console.error(`[decal] fetch ${opts.svgUrl} failed`, err));

  const plane = new THREE.Mesh(
    new THREE.PlaneGeometry(opts.size, opts.size),
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.DoubleSide,
      toneMapped: false,
    })
  );

  // Outer group: position in boat-local axes (offsets move along boat axes).
  const surfaceCenter = center.clone();
  const surfaceQuat = new THREE.Quaternion()
    .setFromUnitVectors(new THREE.Vector3(0, 0, 1), avgN);

  const group = new THREE.Group();
  boat.add(group);
  group.add(plane);

  function applyTransform() {
    group.position.set(
      surfaceCenter.x + opts.offsetX,
      surfaceCenter.y + opts.offsetY,
      surfaceCenter.z + opts.offsetZ
    );
    const userQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(
      (opts.rotationX * Math.PI) / 180,
      (opts.rotationY * Math.PI) / 180,
      (opts.rotationZ * Math.PI) / 180
    ));
    group.quaternion.copy(surfaceQuat).multiply(userQuat);
    plane.position.set(0, 0, 0);
    const w = opts.size;
    const h = opts.size / logoAspect;
    plane.geometry.dispose();
    plane.geometry = new THREE.PlaneGeometry(w, h);
  }

  applyTransform();
}

function loadBoatModel() {
  const loader = new GLTFLoader();

  loader.load(
    boatModelUrl,
    (gltf) => {
      const boatModel = gltf.scene;
      const meshByName = {};

      boatModel.traverse((child) => {
        if (child.isMesh) {
          child.castShadow = false;
          child.receiveShadow = false;
          meshByName[child.name] = child;
          const logoOpts = LOGO_CONFIG[child.name];
          if (logoOpts) {
            applyLogoToMesh(child, logoOpts);
          } else if (child.material) {
            child.material = new THREE.MeshBasicMaterial({
              color: boatRedColor,
              side: THREE.DoubleSide,
              toneMapped: false,
            });
          }
        }
      });

      const box = new THREE.Box3().setFromObject(boatModel);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size);
      box.getCenter(center);

      const maxDimension = Math.max(size.x, size.y, size.z) || 1;
      const scale = 62 / maxDimension;

      boatModel.scale.setScalar(scale);
      boatModel.position.sub(center.multiplyScalar(scale));

      const scaledBox = new THREE.Box3().setFromObject(boatModel);
      boatModel.position.y -= scaledBox.min.y;

      boat.add(boatModel);

      // Plane decals are placed after boat hierarchy is finalized so world
      // matrices reflect the actual mesh placement.
      for (const decal of DECAL_CONFIG) {
        const target = meshByName[decal.target];
        if (target) addLogoPlane(target, decal);
        else console.warn(`[decal] target not found: ${decal.target}`);
      }
    },
    undefined,
    (error) => {
      console.error("Failed to load boat model", error);
    }
  );
}

function updateSun(sky, pmremGenerator, sceneEnv) {
  const phi = THREE.MathUtils.degToRad(90 - parameters.elevation);
  const theta = THREE.MathUtils.degToRad(parameters.azimuth);

  sun.setFromSphericalCoords(1, phi, theta);
  sky.material.uniforms.sunPosition.value.copy(sun);
  water.material.uniforms.sunDirection.value.copy(sun).normalize();

  if (renderTarget) {
    renderTarget.dispose();
  }

  sceneEnv.add(sky);
  renderTarget = pmremGenerator.fromScene(sceneEnv);
  scene.add(sky);
  scene.environment = renderTarget.texture;
}

function onWindowResize() {
  if (isMobile) {
    /* Width may change (orientation), height stays locked */
    const w = container.clientWidth;
    const h = lockedHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
    renderer.domElement.style.width = w + 'px';
    renderer.domElement.style.height = h + 'px';
  } else {
    camera.aspect = container.clientWidth / container.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(container.clientWidth, container.clientHeight);
  }
  if (overlayCamera) {
    overlayCamera.left = -container.clientWidth * 0.5;
    overlayCamera.right = container.clientWidth * 0.5;
    overlayCamera.top = container.clientHeight * 0.5;
    overlayCamera.bottom = -container.clientHeight * 0.5;
    overlayCamera.updateProjectionMatrix();
  }
  onScroll();
}

function onScroll() {
  const scrollable = Math.max(
    document.documentElement.scrollHeight - window.innerHeight,
    0
  );
  scrollState.progress = scrollable > 0 ? window.scrollY / scrollable : 0;
}

function animate() {
  if (!renderer) return; /* crosshair-only on mobile: nothing to render */
  requestAnimationFrame(animate);

  const deltaSeconds = clock.getDelta();

  /* Crosshair-only mode: just render overlay */
  if (crosshairOnly) {
    updateCursorOverlay(deltaSeconds);
    renderer.clear();
    renderer.render(overlayScene, overlayCamera);
    return;
  }

  water.material.uniforms.time.value += deltaSeconds;
  const oceanTime = water.material.uniforms.time.value;
  if (!isMobile) updateCursorOverlay(deltaSeconds);

  if (boat) {
    const driftX = Math.sin(oceanTime * 0.11) * 1.5;
    const driftZ = Math.cos(oceanTime * 0.09 + 0.7) * 1.0;
    const wavePhaseA = oceanTime * 0.85 + driftX * 0.07 + driftZ * 0.05;
    const wavePhaseB = oceanTime * 1.35 - driftX * 0.04 + 1.1;
    const heave = Math.sin(wavePhaseA) * 0.38 + Math.sin(wavePhaseB) * 0.14;

    boat.position.set(boatCenter.x + driftX, boatCenter.y + heave, boatCenter.z + driftZ);

    const pitch = Math.sin(wavePhaseA + 0.65) * 0.04 + Math.sin(wavePhaseB) * 0.012;
    const roll = Math.cos(wavePhaseA - 0.4) * 0.05 + Math.sin(wavePhaseB + 0.5) * 0.018;
    const yaw = -0.16 + Math.PI + oceanTime * 0.035 + Math.sin(oceanTime * 0.2) * 0.012;
    boat.rotation.set(pitch, yaw, roll);

  }

  scrollState.smoothProgress +=
    (scrollState.progress - scrollState.smoothProgress) * (1 - Math.exp(-3 * deltaSeconds));

  const scrollCameraPosition = baseCameraPosition
    .clone()
    .addScaledVector(scrollTravel.camera, scrollState.smoothProgress);
  const scrollCameraTarget = baseCameraTarget
    .clone()
    .addScaledVector(scrollTravel.target, scrollState.smoothProgress);

  if (introCamera.active) {
    introCamera.elapsed = Math.min(introCamera.duration, introCamera.elapsed + deltaSeconds);
    const t = introCamera.elapsed / introCamera.duration;
    const eased = t * t * (3 - 2 * t);

    camera.position.lerpVectors(introCamera.startPosition, introCamera.endPosition, eased);
    const introTarget = new THREE.Vector3().lerpVectors(
      introCamera.startTarget,
      introCamera.endTarget,
      eased
    );
    /* Mobile: fixed FOV, no focal length zoom. Desktop: animate FOV */
    camera.fov = isMobile
      ? introCamera.endFov
      : THREE.MathUtils.lerp(introCamera.startFov, introCamera.endFov, eased);
    camera.updateProjectionMatrix();
    camera.lookAt(introTarget);

    if (t >= 1) {
      introCamera.active = false;
      introScrollBlend.active = true;
      introScrollBlend.progress = 0;
    }
  } else if (introScrollBlend.active) {
    introScrollBlend.progress = Math.min(
      1,
      introScrollBlend.progress + deltaSeconds / introScrollBlend.duration
    );
    const p = introScrollBlend.progress;
    const eased = p * p * p * (p * (p * 6 - 15) + 10);
    camera.position.lerpVectors(introCamera.endPosition, scrollCameraPosition, eased);
    const blendedTarget = new THREE.Vector3().lerpVectors(
      introCamera.endTarget,
      scrollCameraTarget,
      eased
    );
    camera.fov = introCamera.endFov;
    camera.updateProjectionMatrix();
    camera.lookAt(blendedTarget);

    if (introScrollBlend.progress >= 1) {
      introScrollBlend.active = false;
    }
  } else {
    camera.position.copy(scrollCameraPosition);
    camera.fov = introCamera.endFov;
    camera.updateProjectionMatrix();
    camera.lookAt(scrollCameraTarget);
  }

  renderer.clear();
  renderer.render(scene, camera);
  if (!isMobile) {
    renderer.clearDepth();
    renderer.render(overlayScene, overlayCamera);
  }
}
